import {
  ChevronDownIcon,
  Cog6ToothIcon,
  CursorArrowRaysIcon,
  EnvelopeIcon,
  LinkIcon,
  PhoneIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import {
  CTA,
  CTAColor,
  CTAType,
  CTAVariant,
  ImageAssetData,
} from '@superfunnel/types';

import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';
import {
  formFieldUrlWithParamsValidator,
  isValidUrlWithParams,
} from '@superfunnel/utils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import { CSSTextArea } from '../components/FormFields/CSSTextArea';
import FileUploadInput from '../components/FormFields/FileUpload/FileUploadInput';
import QuillField from '../components/QuillField';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';

interface Props {
  item?: CTA;
  open: boolean;
  disableEditAction?: boolean;
  onClose: () => void;
  onSave: (item: CTA) => Promise<void> | void;
}

const createDefaultCTA = (disableEditAction: boolean | undefined): CTA => ({
  id: uid(8),
  title: 'Button',
  variant: 'primary',
  value: '',
  type: disableEditAction ? 'none' : 'link',
});

export default function CreateCTAButtonModal({
  item,
  open,
  disableEditAction,
  onClose,
  onSave,
}: Props) {
  const { show } = useContext(SnackbarContext);

  const [data, setData] = useState<CTA>(() =>
    createDefaultCTA(disableEditAction),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error>();
  const [rndKey, setRndKey] = useState(0);

  const { forms } = useContext(DashboardContext);

  useEffect(() => {
    setData(
      item
        ? JSON.parse(JSON.stringify(item))
        : createDefaultCTA(disableEditAction),
    );
    setError(undefined);
    setIsSaving(false);
  }, [open, item, disableEditAction]);

  const createCTA = useCallback(async () => {
    setError(undefined);

    if (!data.title) {
      setError(new Error('Button title is required'));
      show('Button title is required');
      return;
    }

    if (!data.type && !disableEditAction) {
      setError(new Error('Button action is required'));
      show('Button action is required');
      return;
    }

    if (data.type === 'link' && !data.value && !disableEditAction) {
      setError(new Error('Link url is required'));
      show('Link url is required');
      return;
    }

    if (
      data.type === 'link' &&
      !isValidUrlWithParams(data.value) &&
      !disableEditAction
    ) {
      setError(new Error('Link url is not valid'));
      show('Link url is not valid');
      return;
    }

    if (
      data.type === 'form' &&
      (!data.value || !forms?.some((f) => f.id === data.value)) &&
      !disableEditAction
    ) {
      setError(new Error('Please select a form'));
      show('Please select a form');
      return;
    }

    if (data.type !== 'form') {
      delete data.formData;
    } else {
      data.value = data.value || data.formData?.formId || '';
      delete data.formData;
    }

    const res = onSave(data as any);
    if (res && 'then' in res) {
      setIsSaving(true);
      res.catch((e) => {
        setError(e);
        Sentry.captureException(e);
      });
      res.finally(() => setIsSaving(false));
    }
  }, [data, disableEditAction, forms, onSave, show]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: isSaving,
        onClick: () => onClose(),
      },
      {
        title: item ? 'Update' : 'Create',
        type: 'primary',
        loading: isSaving,
        onClick: () => createCTA(),
      },
    ],
    [createCTA, isSaving, item, onClose],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      title={item ? 'Update CTA button' : 'Create a new CTA button'}
    >
      <div key={data.id} className="mt-0 flex flex-col gap-4">
        <QuillField
          title="Title"
          key={data.id}
          value={data.title}
          onChange={(value) => {
            setData((d) => {
              d.title = value as any;
              return { ...d };
            });
          }}
        />
        <QuillField
          title="Text Below (Optional)"
          key={rndKey + '-' + data.id}
          value={data.textBelow || ''}
          placeholder="Optional text to display below the button..."
          trailing={
            <div className="flex w-full items-center justify-end gap-2">
              <Button
                size="icon-xs"
                variant="ghost"
                onClick={() => {
                  setData((d) => {
                    d.textBelow = '';
                    return { ...d };
                  });
                  setRndKey((k) => k + 1);
                }}
              >
                <TrashIcon className="h-5 w-5" />
              </Button>
            </div>
          }
          onChange={(value) => {
            setData((d) => {
              d.textBelow = value as any;
              return { ...d };
            });
          }}
        />
        {!disableEditAction && (
          <>
            <div className="flex flex-col justify-start gap-1">
              <FieldTitle title="Action" />
              <Select
                value={data.type}
                onValueChange={(e: CTAType) => {
                  setData((d) => ({
                    ...d,
                    type: e,
                  }));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">
                    <span className="flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      Link
                    </span>
                  </SelectItem>
                  <SelectItem value="phone">
                    <span className="flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4" />
                      Phone
                    </span>
                  </SelectItem>
                  <SelectItem value="email">
                    <span className="flex items-center gap-2">
                      <EnvelopeIcon className="h-4 w-4" />
                      Email
                    </span>
                  </SelectItem>
                  <SelectItem value="form">
                    <span className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      Form
                    </span>
                  </SelectItem>
                  <SelectItem value="none">
                    <span className="flex items-center gap-2">
                      <CursorArrowRaysIcon className="h-4 w-4" />
                      None
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {data.type === 'link' ? (
              <Input
                value={data.value}
                type="url"
                placeholder="https://google.com/...."
                onChange={(e) => {
                  setData((d) => {
                    d.value = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlWithParamsValidator(data.value)}
              />
            ) : (
              <></>
            )}
            {data.type === 'phone' ? (
              <Input
                type="tel"
                value={data.value}
                placeholder="+9720501234567"
                onChange={(e) => {
                  setData((d) => {
                    d.value = e.target.value;
                    return { ...d };
                  });
                }}
              />
            ) : (
              <></>
            )}
            {data.type === 'email' ? (
              <Input
                type="email"
                value={data.value}
                placeholder="yourmail@example.com"
                onChange={(e) => {
                  setData((d) => {
                    d.value = e.target.value;
                    return { ...d };
                  });
                }}
              />
            ) : (
              <></>
            )}
            {data.type === 'form' ? (
              <div className="flex flex-col justify-start gap-1">
                <FieldTitle title="Lead form" />
                <Select
                  value={(data.value || data.formData?.formId) ?? ''}
                  onValueChange={(e: string) => {
                    const form = forms?.find((f) => f.id === e);
                    if (!form) return;
                    setData((d) => {
                      d.value = e;
                      delete d.formData;

                      return { ...d };
                    });
                  }}
                  disabled={!forms}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a lead form.." />
                  </SelectTrigger>
                  <SelectContent>
                    {forms?.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.data().name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <></>
            )}
          </>
        )}

        <div>
          <FieldTitle className="mb-2" title={'Style'} />
          <ToggleGroup
            type="single"
            variant="outline"
            value={data.variant ?? 'primary'}
            onValueChange={(variant: CTAVariant) => {
              if (variant) setData((d) => ({ ...d, variant }));
            }}
          >
            <ToggleGroupItem value="primary">Primary</ToggleGroupItem>
            <ToggleGroupItem value="secondary">Secondary</ToggleGroupItem>
            <ToggleGroupItem value="text">Text</ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div>
          <FieldTitle className="mb-2" title={'Color'} />
          <ToggleGroup
            type="single"
            variant="outline"
            value={data.color ?? 'default'}
            onValueChange={(color: CTAColor | 'default') => {
              if (!color) return;
              setData((d) => {
                const newData = { ...d };

                if (color === 'default') {
                  delete newData.color;
                } else {
                  newData.color = color;
                }

                return newData;
              });
            }}
          >
            <ToggleGroupItem value="default">Default</ToggleGroupItem>
            <ToggleGroupItem value="colored">Colored</ToggleGroupItem>
            <ToggleGroupItem value="white">White</ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div>
          <FieldTitle className="mb-2" title={'Start Icon'} />

          <FileUploadInput
            image={data?.startIcon}
            types={['image']}
            onSelected={(image) => {
              setData((d) => {
                const newData = { ...d };

                if (image) {
                  newData.startIcon = image as ImageAssetData;
                } else {
                  delete newData.startIcon;
                }

                return newData;
              });
            }}
          />
        </div>
        <div>
          <FieldTitle className="mb-2" title={'End Icon'} />

          <FileUploadInput
            image={data?.endIcon}
            types={['image']}
            onSelected={(image) => {
              setData((d) => {
                const newData = { ...d };

                if (image) {
                  newData.endIcon = image as ImageAssetData;
                } else {
                  delete newData.endIcon;
                }

                return newData;
              });
            }}
          />
        </div>
        <div>
          <Collapsible className="group">
            <CollapsibleTrigger className="flex items-center gap-1 py-2">
              <Cog6ToothIcon className="h-4 w-4" />
              Advanced Settings
              <div className="flex-1" />
              <ChevronDownIcon className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 flex flex-col gap-4">
              {data.type === 'link' && (
                <div className="flex flex-col justify-start gap-1">
                  <FieldTitle title="Link Target" />
                  <Select
                    value={data.target ?? '_blank'}
                    onValueChange={(e: '_blank' | '_self') => {
                      setData((d) => ({
                        ...d,
                        target: e,
                      }));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Link Target" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_blank">Open in new tab</SelectItem>
                      <SelectItem value="_self">Open in same tab</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <CSSTextArea
                title="Custom CSS"
                className="h-32"
                value={data.buttonCSS}
                onChange={(value) => {
                  setData((d) => {
                    d.buttonCSS = value;
                    return { ...d };
                  });
                }}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
